import { canvasFontStack } from "../style/surfFonts.js";
import { resolveFittedTextStyle } from "./textStyle.js";
import { TEXT_OVERRIDE_KEY } from "./textStyle.js";
import { SURF_RASTER_KINDS } from "../illustration/rasterKind.js";
import { clampCrop, isValidCrop } from "./imageCrop.js";
/**
 * Canonical SurfLayer.style keys per layer type.
 * Unknown/orphan keys are dropped by normalizeLayerStyle.
 */
export const LAYER_STYLE_KEYS = {
    text: [
        TEXT_OVERRIDE_KEY,
        "slotRole",
        "renderMode",
        "rasterKind",
        "rasterCustomPrompt",
        "rasterAssetKey",
        "rasterAspect",
        "rasterStale",
        "rasterBackdrop",
        "rasterBackdropOverride",
        "cutout",
        "crop",
        "useDeckBackdrop",
        "refineInstructions",
        "imageReferenceKey",
        "boxBg",
        "boxBgOpacity",
    ],
    image: [
        "textRender",
        "bakedText",
        "imageSubject",
        "cutout",
        "crop",
        "useDeckBackdrop",
        "refineInstructions",
        "imageReferenceKey",
        "rasterBackdropOverride",
    ],
    shape: ["fill", "stroke", "strokeWidth"],
    icon: [],
    table: ["table"],
    chart: ["chart"],
    graphic: ["graphic"],
};
const RASTER_KIND_IDS = new Set(SURF_RASTER_KINDS.map((k) => k.id));
function coerceBooleanFlag(value) {
    return value === true ? true : undefined;
}
function coerceHex(value) {
    if (typeof value !== "string")
        return undefined;
    const raw = value.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(raw))
        return raw.toUpperCase();
    if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
        const h = raw.slice(1);
        return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
    }
    return undefined;
}
function coerceStyleEntry(key, value, layerType) {
    if (value === undefined || value === null || value === "")
        return undefined;
    if (key === "cutout" || key === "useDeckBackdrop" || key === "rasterStale") {
        return coerceBooleanFlag(value);
    }
    if (key === "renderMode") {
        return value === "raster" ? "raster" : undefined;
    }
    if (key === "textRender") {
        return value === "in_image" ? "in_image" : undefined;
    }
    if (key === "rasterKind") {
        const id = String(value);
        if (!RASTER_KIND_IDS.has(id) || id === "freeform")
            return undefined;
        return id;
    }
    if (key === "rasterAspect") {
        return value === "1:1" || value === "3:2" || value === "2:3" ? value : undefined;
    }
    if (key === "rasterBackdrop" || key === "rasterBackdropOverride" || key === "boxBg") {
        return coerceHex(value);
    }
    if (key === "boxBgOpacity" && typeof value === "number" && Number.isFinite(value)) {
        return Math.min(1, Math.max(0, value));
    }
    if (key === "rasterAssetKey" || key === "imageReferenceKey") {
        const s = typeof value === "string" ? value.trim() : "";
        return s || undefined;
    }
    if (key === "refineInstructions" ||
        key === "imageSubject" ||
        key === "slotRole" ||
        key === "rasterCustomPrompt") {
        const s = typeof value === "string" ? value.trim() : "";
        return s || undefined;
    }
    if (key === TEXT_OVERRIDE_KEY) {
        if (!value || typeof value !== "object")
            return undefined;
        return value;
    }
    if (key === "crop") {
        return isValidCrop(value) ? clampCrop(value) : undefined;
    }
    if (key === "bakedText" || key === "table" || key === "chart" || key === "graphic") {
        if (!value || typeof value !== "object")
            return undefined;
        return value;
    }
    if (layerType === "shape" && (key === "fill" || key === "stroke")) {
        return coerceHex(value) ?? (typeof value === "string" ? value : undefined);
    }
    if (key === "strokeWidth" && typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    return undefined;
}
/** Drop orphan style keys and coerce supported fields (on-read / on-commit). */
export function normalizeLayerStyle(layer) {
    const allowed = LAYER_STYLE_KEYS[layer.type] ?? [];
    const style = layer.style ?? {};
    const next = {};
    for (const key of allowed) {
        if (!(key in style))
            continue;
        const coerced = coerceStyleEntry(key, style[key], layer.type);
        if (coerced !== undefined)
            next[key] = coerced;
    }
    return { ...layer, style: next };
}
export function layerRenderStyle(layer, theme, stageHeight) {
    const ts = resolveFittedTextStyle(layer, theme);
    const fontSize = Math.max(12, ts.sizeRel * stageHeight);
    return {
        fill: layer.type === "shape" ? theme.palette.accent : undefined,
        fontFamily: canvasFontStack(ts.font),
        fontSize,
        fontStyle: ts.weight >= 700 ? "bold" : "normal",
        fillText: ts.color,
        align: ts.align,
        lineHeight: ts.lineHeight,
        letterSpacing: `${ts.tracking}em`,
        textTransform: ts.case === "upper" ? "uppercase" : "none",
    };
}
/** Text widget box background (color + opacity) for overlay on colored slides. Null = none. */
export function getTextBoxBackground(layer) {
    const raw = layer.style?.boxBg;
    if (typeof raw !== "string" || !/^#[0-9A-Fa-f]{6}$/.test(raw.trim()))
        return null;
    const o = layer.style?.boxBgOpacity;
    const opacity = typeof o === "number" && Number.isFinite(o) ? Math.min(1, Math.max(0, o)) : 1;
    return { color: raw.trim().toUpperCase(), opacity };
}
export { resolveTextStyle, resolveFittedTextStyle, getTextOverride, setTextOverride, clearTextOverride } from "./textStyle.js";
